import React from 'react';
import type { AccountOption } from './SelectAccountStep';
import type { CampaignType, CampaignStatus } from './CampaignBasicsStep';
import type { BiddingStrategy } from './BudgetBiddingStep';
import type { NetworkSettings, TargetingSettings } from './NetworkSettingsStep';
import { GEO_TARGET_OPTIONS, LANGUAGE_OPTIONS } from './networkOptions';
import type { ShoppingSettings } from './ShoppingSettingsStep';
import type { DemandGenSettings, ChannelStrategy } from './demandGenSettings';
import { formatAccountId } from '@/utils';
import { BUDGET_CONFIG } from '@/config/constants';

interface ReviewStepProps {
  selectedAccount: AccountOption | null;
  campaignName: string;
  campaignType: CampaignType;
  enableAiMax: boolean;
  status: CampaignStatus;
  containsEuPoliticalAdvertising: boolean;
  dailyBudget: string;
  biddingStrategy: BiddingStrategy;
  targetCpaValue: string;
  targetRoasValue: string;
  targetImpressionShareLocation: string;
  targetImpressionShareLocationFraction: string;
  targetImpressionShareCpcCeiling: string;
  networkSettings: NetworkSettings;
  targetingSettings: TargetingSettings;
  shoppingSettings: ShoppingSettings;
  demandGenSettings?: DemandGenSettings;
  startDate: string;
  endDate: string;
  hasEndDate: boolean;
  onEdit: (step: number) => void;
}

// Helper to format campaign type for display
const campaignTypeLabels: Record<CampaignType, string> = {
  SEARCH: 'Search',
  DISPLAY: 'Display',
  PERFORMANCE_MAX: 'Performance Max',
  SHOPPING: 'Shopping',
  DEMAND_GEN: 'Demand Gen',
};

// Helper to format bidding strategy for display
const biddingStrategyLabels: Record<BiddingStrategy, string> = {
  MANUAL_CPC: 'Manual CPC',
  MAXIMIZE_CLICKS: 'Maximize Clicks',
  TARGET_CPA: 'Target CPA',
  TARGET_ROAS: 'Target ROAS',
  TARGET_IMPRESSION_SHARE: 'Target Impression Share',
  MAXIMIZE_CONVERSIONS: 'Maximize Conversions',
  MAXIMIZE_CONVERSION_VALUE: 'Maximize Conversion Value',
};

// Helper to format status for display
const statusLabels: Record<CampaignStatus, string> = {
  ENABLED: 'Enabled',
  PAUSED: 'Paused',
};

// Helper to format geo targets for display
const formatGeoTargets = (geoIds?: number[]): string => {
  if (!geoIds || geoIds.length === 0) return 'USA (default)';
  const names = geoIds
    .map((id) => GEO_TARGET_OPTIONS.find((g) => g.id === id)?.name)
    .filter(Boolean);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

// Helper to format languages for display
const formatLanguages = (langIds?: number[]): string => {
  if (!langIds || langIds.length === 0) return 'English (default)';
  const names = langIds
    .map((id) => LANGUAGE_OPTIONS.find((l) => l.id === id)?.name)
    .filter(Boolean);
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
};

// Helper to format channel strategy for display
const channelStrategyLabels: Record<ChannelStrategy, string> = {
  ALL_CHANNELS: 'All Channels',
  ALL_OWNED_AND_OPERATED_CHANNELS: 'Google-Owned Channels Only',
  SELECTED_CHANNELS: 'Custom Selection',
};

// Helper to format enabled channels for display
const formatEnabledChannels = (settings: DemandGenSettings): string => {
  const { channelSettings } = settings;
  if (channelSettings.channelStrategy !== 'SELECTED_CHANNELS') {
    return channelStrategyLabels[channelSettings.channelStrategy];
  }

  const enabledChannels: string[] = [];
  if (channelSettings.youtubeInStream) enabledChannels.push('YouTube In-Stream');
  if (channelSettings.youtubeInFeed) enabledChannels.push('YouTube In-Feed');
  if (channelSettings.youtubeShorts) enabledChannels.push('YouTube Shorts');
  if (channelSettings.discover) enabledChannels.push('Discover');
  if (channelSettings.gmail) enabledChannels.push('Gmail');
  if (channelSettings.display) enabledChannels.push('Display Network');
  // maps not yet supported in Google Ads API v22/v23

  if (enabledChannels.length === 0) return 'None selected';
  if (enabledChannels.length <= 2) return enabledChannels.join(', ');
  return `${enabledChannels.slice(0, 2).join(', ')} +${enabledChannels.length - 2} more`;
};

export const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedAccount,
  campaignName,
  campaignType,
  enableAiMax,
  status,
  containsEuPoliticalAdvertising,
  dailyBudget,
  biddingStrategy,
  targetCpaValue,
  targetRoasValue,
  targetImpressionShareLocation,
  targetImpressionShareLocationFraction,
  targetImpressionShareCpcCeiling,
  networkSettings,
  targetingSettings,
  shoppingSettings,
  demandGenSettings,
  startDate,
  endDate,
  hasEndDate,
  onEdit,
}) => {
  const budgetValue = parseFloat(dailyBudget) || 0;
  const targetCpa = parseFloat(targetCpaValue) || 0;
  const targetRoas = parseFloat(targetRoasValue) || 0;
  const targetImpressionShareFraction = parseFloat(targetImpressionShareLocationFraction) || 0;
  const targetImpressionShareCeiling = parseFloat(targetImpressionShareCpcCeiling) || 0;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Review Your Google Ads Campaign</h3>
        <p className="text-sm text-gray-600">
          Review your campaign settings before creating. Click any section to edit.
        </p>
      </div>

      {/* Account section intentionally has no Edit button. Changing the
          account mid-wizard would invalidate every later step (the budget
          rules, the available bidding strategies, etc.), so the user must
          pick correctly in the OmniAccountPicker / Step 1 or close and
          start over. */}
      <ReviewSection
        title="Google Ads Account"
        items={[
          {
            label: 'Account Name',
            value: selectedAccount?.name || 'Not selected',
          },
          {
            label: 'Account ID',
            value: selectedAccount ? formatAccountId(selectedAccount.id) : 'Not selected',
          },
        ]}
      />

      {/* Campaign Basics */}
      <ReviewSection
        title="Campaign Basics"
        onEdit={() => onEdit(2)}
        items={[
          { label: 'Campaign Name', value: campaignName || 'Not set' },
          {
            label: 'Campaign Type',
            value: campaignType === 'SEARCH' && enableAiMax ? 'AI Max for Search' : campaignTypeLabels[campaignType]
          },
          { label: 'Initial Status', value: statusLabels[status] },
          { label: 'EU Political Advertising', value: containsEuPoliticalAdvertising ? 'Yes' : 'No' },
        ]}
      />

      {/* Budget & Bidding */}
      <ReviewSection
        title="Budget & Bidding"
        onEdit={() => onEdit(3)}
        items={[
          {
            label: 'Daily Budget',
            value: budgetValue > 0 ? `$${budgetValue.toFixed(2)}` : 'Not set',
            detail:
              budgetValue > 0
                ? `Estimated ${BUDGET_CONFIG.TIME_SPAN_LABEL}: $${(budgetValue * BUDGET_CONFIG.AVERAGE_DAYS_PER_STANDARD_TIME_SPAN).toFixed(2)}`
                : undefined,
          },
          {
            label: 'Bidding Strategy',
            value: biddingStrategyLabels[biddingStrategy],
            detail:
              biddingStrategy === 'TARGET_CPA' && targetCpa > 0
                ? `Target: $${targetCpa.toFixed(2)}`
                : biddingStrategy === 'TARGET_ROAS' && targetRoas > 0
                ? `Target: ${targetRoas.toFixed(2)}x`
                : biddingStrategy === 'TARGET_IMPRESSION_SHARE' && targetImpressionShareFraction > 0
                ? `Target: ${targetImpressionShareFraction.toFixed(0)}% ${
                    targetImpressionShareLocation === 'ANYWHERE_ON_PAGE'
                      ? 'anywhere on page'
                      : targetImpressionShareLocation === 'TOP_OF_PAGE'
                      ? 'top of page'
                      : 'absolute top of page'
                  }${targetImpressionShareCeiling > 0 ? `, max CPC: $${targetImpressionShareCeiling.toFixed(2)}` : ''}`
                : undefined,
          },
        ]}
      />

      {/* Targeting & Network Settings (only for Search campaigns) */}
      {campaignType === 'SEARCH' && (
        <ReviewSection
          title="Targeting & Networks"
          onEdit={() => onEdit(4)}
          items={[
            {
              label: 'Locations',
              value: formatGeoTargets(targetingSettings?.geoTargetIds),
            },
            {
              label: 'Languages',
              value: formatLanguages(targetingSettings?.languageIds),
            },
            {
              label: 'Google Search',
              value: networkSettings.targetGoogleSearch ? 'Enabled' : 'Disabled',
              highlight: !networkSettings.targetGoogleSearch,
            },
            {
              label: 'Search Partners',
              value: networkSettings.targetSearchNetwork ? 'Enabled' : 'Disabled',
            },
            {
              label: 'Display Network',
              value: networkSettings.targetContentNetwork ? 'Enabled' : 'Disabled',
            },
          ]}
        />
      )}

      {/* Shopping Settings (only for Shopping campaigns) */}
      {campaignType === 'SHOPPING' && (
        <ReviewSection
          title="Shopping Settings"
          onEdit={() => onEdit(4)}
          items={[
            {
              label: 'Merchant Center ID',
              value: shoppingSettings.merchantId || 'Not set',
            },
            {
              label: 'Sales Country',
              value: shoppingSettings.salesCountry || 'Not set',
            },
            {
              label: 'Campaign Priority',
              value: shoppingSettings.campaignPriority === 0
                ? 'Low'
                : shoppingSettings.campaignPriority === 1
                ? 'Medium'
                : 'High',
            },
            {
              label: 'Local Inventory Ads',
              value: shoppingSettings.enableLocal ? 'Enabled' : 'Disabled',
            },
          ]}
        />
      )}

      {/* Demand Gen Settings (only for Demand Gen campaigns) */}
      {campaignType === 'DEMAND_GEN' && demandGenSettings && (
        <ReviewSection
          title="Demand Gen Settings"
          onEdit={() => onEdit(4)}
          items={[
            {
              label: 'Channels',
              value: formatEnabledChannels(demandGenSettings),
              detail: demandGenSettings.channelSettings.channelStrategy === 'SELECTED_CHANNELS'
                ? 'Custom channel selection'
                : undefined,
            },
            {
              label: 'Optimized Targeting',
              value: demandGenSettings.optimizedTargeting ? 'Enabled' : 'Disabled',
              highlight: !demandGenSettings.optimizedTargeting,
            },
            {
              label: 'Locations',
              value: formatGeoTargets(targetingSettings?.geoTargetIds),
            },
            {
              label: 'Languages',
              value: formatLanguages(targetingSettings?.languageIds),
            },
          ]}
        />
      )}

      {/* Schedule */}
      <ReviewSection
        title="Schedule"
        onEdit={() => onEdit(5)}
        items={[
          {
            label: 'Start Date',
            value: startDate ? new Date(startDate).toLocaleDateString() : 'Not set',
          },
          {
            label: 'End Date',
            value: hasEndDate && endDate ? new Date(endDate).toLocaleDateString() : 'No end date',
          },
          ...(startDate && hasEndDate && endDate
            ? [
                {
                  label: 'Duration',
                  value: (() => {
                    const start = new Date(startDate);
                    const end = new Date(endDate);
                    const diffTime = Math.abs(end.getTime() - start.getTime());
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                  })(),
                },
              ]
            : []),
        ]}
      />

      {/* Important Notice */}
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <div className="flex">
          <svg
            className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-yellow-800">Before You Create</h4>
            <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
              <li>
                This will create a campaign shell. You'll need to add ad groups, keywords, and ads
                separately.
              </li>
              <li>Campaign will be created with status: {statusLabels[status]}</li>
              {status === 'ENABLED' && (
                <li className="font-semibold">
                  Ads may start serving immediately after creation once you have ad groups set up.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable review section component
interface ReviewSectionProps {
  title: string;
  /** Omit to render a read-only section (no Edit button). Used for the
   *  Account section, which can't be changed mid-wizard. */
  onEdit?: () => void;
  items: Array<{
    label: string;
    value: string;
    detail?: string;
    highlight?: boolean;
  }>;
}

const ReviewSection: React.FC<ReviewSectionProps> = ({ title, onEdit, items }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {onEdit && (
          <button
            onClick={onEdit}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium focus:outline-none focus:underline"
          >
            Edit
          </button>
        )}
      </div>
      <dl className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex flex-col sm:flex-row sm:justify-between text-sm gap-1 sm:gap-2">
            <dt className="text-gray-600">{item.label}:</dt>
            <dd className="font-medium text-gray-900 sm:text-right">
              <span className={item.highlight ? 'text-yellow-600' : ''}>{item.value}</span>
              {item.detail && (
                <span className="block text-xs text-gray-500 mt-0.5">{item.detail}</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
